/**
 * Renderer-independent limits and asset checks for the NeuroViz viewport.
 *
 * Scientific source files are deliberately not browser assets. The API should
 * expose derived chunks/tiles instead; these checks are a second line of
 * defence when a manifest is incomplete or stale.
 */
export const MAX_POINTS_PER_CHUNK = 10_000;
export const MAX_VISIBLE_POINTS = 50_000;
export const MAX_VOLUME_TILES = 64;
/** Only presentation properties are clamped; source coordinates are never fitted. */
export const safeOpacity = (value, fallback = 1) => typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : fallback;
export const RAW_SOURCE_EXTENSIONS = Object.freeze(['h5ad', 'nwb', 'nrrd', 'nii', 'nii.gz']);
export const RENDERABLE_EXTENSIONS = Object.freeze(['glb', 'gltf', 'pnts', 'bin', 'zarr', 'json', 'ktx2', 'png', 'webp']);

const extension = (uri = '') => {
  const value = String(uri).split(/[?#]/, 1)[0].toLowerCase();
  return RAW_SOURCE_EXTENSIONS.find((item) => value.endsWith(`.${item}`))
    || value.split('.').pop();
};

export const isRawScientificSource = (uri) => RAW_SOURCE_EXTENSIONS.includes(extension(uri));

export function isBrowserRenderableAsset(asset, { kind = 'mesh' } = {}) {
  const uri = asset?.browser_url || asset?.storage_uri;
  if (!asset || asset.state !== 'ready' || !uri || !(/^(https?:\/\/|\/)/i.test(String(uri))) || isRawScientificSource(uri)) return false;
  if (kind === 'volume' || kind === 'labelmap') return Boolean(asset.tile_manifest || asset.tiles || asset.storage_format === 'zarr');
  return Boolean(asset.format ? RENDERABLE_EXTENSIONS.includes(String(asset.format).toLowerCase()) : extension(uri) === 'glb');
}

export function clampBounds(bounds, fallback = null) {
  const min = bounds?.min?.slice?.(0, 3).map(Number);
  const max = bounds?.max?.slice?.(0, 3).map(Number);
  if (!min || !max || min.length !== 3 || max.length !== 3 || [...min, ...max].some((value) => !Number.isFinite(value))) return fallback;
  return { min: min.map((value, index) => Math.min(value, max[index])), max: max.map((value, index) => Math.max(value, min[index])) };
}

export const pointInBounds = (position, bounds) => {
  const safe = clampBounds(bounds);
  return !safe || (Array.isArray(position) && position.length >= 3 && position.slice(0, 3).every((value, index) => Number(value) >= safe.min[index] && Number(value) <= safe.max[index]));
};

/** Cap work before typed arrays/GPU attributes are allocated. */
export function chunkItems(items = [], { chunkSize = MAX_POINTS_PER_CHUNK, maxItems = MAX_VISIBLE_POINTS, bounds = null, getPosition = (item) => item?.position } = {}) {
  const size = Math.max(1, Math.min(MAX_POINTS_PER_CHUNK, Number(chunkSize) || MAX_POINTS_PER_CHUNK));
  const chunks = [];
  let chunk = [];
  for (const item of items) {
    if (chunks.length * size + chunk.length >= maxItems) break;
    if (bounds && !pointInBounds(getPosition(item), bounds)) continue;
    chunk.push(item);
    if (chunk.length === size) { chunks.push(chunk); chunk = []; }
  }
  if (chunk.length) chunks.push(chunk);
  return chunks;
}

export function limitVolumeTiles(tiles = [], { maxTiles = MAX_VOLUME_TILES, bounds = null } = {}) {
  return tiles.filter((tile) => !bounds || pointInBounds(tile?.center || tile?.position, bounds)).slice(0, Math.max(1, Number(maxTiles) || MAX_VOLUME_TILES));
}

let cachedWebGLSupport;

export function detectWebGLSupport() {
  if (typeof document === 'undefined') return false;
  if (cachedWebGLSupport !== undefined) return cachedWebGLSupport;
  try {
    const canvas = document.createElement('canvas');
    // vtk.js and the installed deck.gl require WebGL2; WebGL1 is not enough.
    const context = canvas.getContext('webgl2');
    cachedWebGLSupport = Boolean(context);
    // This is a capability probe, not a renderer. Release its temporary
    // context immediately so React renders cannot exhaust Chrome's context
    // budget and evict the atlas canvas.
    context?.getExtension('WEBGL_lose_context')?.loseContext();
    return cachedWebGLSupport;
  } catch {
    cachedWebGLSupport = false;
    return cachedWebGLSupport;
  }
}

export function createPerformanceInstrumentation({ target = typeof performance !== 'undefined' ? performance : null, onMeasure = null } = {}) {
  const started = new Map();
  const mark = (name) => { const time = target?.now?.() ?? Date.now(); started.set(name, time); try { target?.mark?.(`neuroviz-${name}`); } catch { /* performance marks are best effort */ } return time; };
  const measure = (name, start = name) => { const duration = Math.max(0, (target?.now?.() ?? Date.now()) - (started.get(start) ?? 0)); try { target?.measure?.(`neuroviz-${name}`, `neuroviz-${start}`); } catch { /* a reduced performance API is valid */ } onMeasure?.({ name, duration }); return duration; };
  return { mark, measure, firstRender: () => measure('first-render', 'first-render-start'), interaction: (type = 'interaction') => measure(type, `${type}-start`) };
}

/** Explicitly installed diagnostic probe, never enabled by normal page loads.
 * Count actual distinct WebGL objects, not React renders or getContext calls.
 * Loss/restoration are native events; release is not inferred from unmount.
 */
export function installGraphicsLifecycleProbe(prototype = HTMLCanvasElement.prototype) {
  const original=prototype.getContext, seen=new WeakSet(), live=new Set(), detach=[];
  let created=0,released=0,restored=0,peak=0;
  const history=[];
  const snapshot=()=>({created,released,restored,live:live.size,peak});
  const record=()=>{if(history.length<1000)history.push(snapshot());};
  function wrapped(kind,...args) {
    const context=original.call(this,kind,...args);
    if(context&&/^(webgl2?|experimental-webgl)$/.test(kind)&&!seen.has(context)) {
      seen.add(context);live.add(context);created++;peak=Math.max(peak,live.size);record();
      const lost=()=>{if(live.delete(context)){released++;record();}};
      const restore=()=>{if(!live.has(context)){live.add(context);restored++;peak=Math.max(peak,live.size);record();}};
      this.addEventListener('webglcontextlost',lost);this.addEventListener('webglcontextrestored',restore);
      detach.push(()=>{this.removeEventListener('webglcontextlost',lost);this.removeEventListener('webglcontextrestored',restore);});
    }
    return context;
  }
  prototype.getContext=wrapped;
  return {snapshot,report:()=>({...snapshot(),history:history.map(row=>({...row}))}),stop(){if(prototype.getContext===wrapped)prototype.getContext=original;detach.splice(0).forEach(fn=>fn());live.clear();}};
}

// Full collections share these caps across the entire delivery, never per tile.
export const COLLECTION_CAPS = Object.freeze({points: 1000000, tiles: 64, bytes: 512 * 1024 * 1024});

/** Instrument real renderer construction/deletion only when explicitly injected. */
export function createRendererLifecycleProbe() {
  const live = new Set(), wrappers = new WeakMap(); let created = 0, released = 0, peak = 0;
  return {
    track(renderer) {
      if (!renderer || typeof renderer.delete !== 'function') throw new TypeError('Renderer delete required');
      if (wrappers.has(renderer)) return wrappers.get(renderer);
      live.add(renderer); created++; peak = Math.max(peak, live.size);
      // vtk public APIs are frozen. Return this adapter from the real factory;
      // mutating the original would throw and interrupt renderer construction.
      const wrapper = {...renderer, delete(...args) {
        const result = renderer.delete(...args);
        if (live.delete(renderer)) released++;
        return result;
      }};
      wrappers.set(renderer, wrapper);
      wrappers.set(wrapper, wrapper);
      return wrapper;
    },
    snapshot: () => ({created, released, live: live.size, peak}),
  };
}
